/**
 * Аккаунт владельца, проверяющего игру инструментами `/admin` (MADR 0016). Сервер говорит о нём в профиле игрока.
 * Устройство запоминает это, чтобы не отправить аналитику до входа, а на новый сброс стирает флаги первых показов:
 * они лежат в localStorage, и сервер их сам не очистит.
 */
import type { PlayerProfile } from "@newfungame/contracts";
import { hintsStore, initialHintsState } from "../ui/hints/model";
import { appStore } from "./store";

export const TEST_ACCOUNT_KEY = "nfg:testAccount";
export const TEST_RESET_KEY = "nfg:testReset";

/** Ключи и начала ключей того, что показывается один раз: подсказки, вопросы о праве и ярлыке, вехи аналитики. */
export const FIRST_SHOW_KEYS = ["nfg:writeAccess", "nfg:homeScreen"];
export const FIRST_SHOW_PREFIXES = ["nfg:hint:", "nfg.a.", "nfg:introHero:"];

type Keys = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;
type Read = Pick<Storage, "getItem">;

/**
 * Молчит ли аналитика: после входа решает аккаунт текущего входа, до входа — последний вход на устройстве
 * (в Telegram localStorage общий для всех аккаунтов клиента, флаг другого игрока его не глушит).
 */
export function isTestAccount(player: PlayerProfile | null, storage: Read | null): boolean {
  if (player) return player.testAccount;
  try {
    return storage?.getItem(TEST_ACCOUNT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Запоминает аккаунт (обычный снимает метку) и выполняет ещё не виденный сброс. `true` — флаги стёрты. */
export function applyTestAccount(player: PlayerProfile, storage: Keys | null): boolean {
  if (!storage) return false;
  if (!player.testAccount) {
    try {
      storage.removeItem(TEST_ACCOUNT_KEY);
    } catch {
      // без localStorage метки и не было
    }
    return false;
  }
  try {
    storage.setItem(TEST_ACCOUNT_KEY, "1");
    const reset = player.testResetAt === null ? null : String(player.testResetAt);
    if (reset === null || storage.getItem(TEST_RESET_KEY) === reset) return false;
    const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const key of keys) {
      if (key && (FIRST_SHOW_KEYS.includes(key) || FIRST_SHOW_PREFIXES.some((p) => key.startsWith(p)))) {
        storage.removeItem(key);
      }
    }
    storage.setItem(TEST_RESET_KEY, reset);
    return true;
  } catch {
    return false; // без localStorage показывать нечего стирать
  }
}

/** Следит за профилем: при новом сбросе подсказки возвращаются сразу, без перезагрузки. */
export function watchTestAccount(): () => void {
  const check = () => {
    const player = appStore.get().player;
    if (!player) return;
    try {
      if (applyTestAccount(player.player, window.localStorage)) hintsStore.set(initialHintsState());
    } catch {
      // localStorage недоступен: стирать нечего
    }
  };
  check();
  return appStore.subscribe((state, prev) => {
    if (state.player !== prev.player) check();
  });
}
